import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';

interface PersistedErrorState {
    error: string
    timestamp: number
}


interface ErrorSetterContext {
    errorList: PersistedErrorState[]
    reportError: (newError: string) => void
    isDefault: boolean
}

const ErrorContext = createContext<ErrorSetterContext>({
    errorList: [],
    reportError: (newError) => {},
    isDefault: true
});

const closeX = (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
)

const TIMEOUT_SECONDS = 20

export function ErrorViewer() {
    const {errorList, isDefault} = useContext(ErrorContext)
    const [activeError, setActiveError] = useState<PersistedErrorState | null>(null)
    const [remaining, setRemaining] = useState<number>(0)
    const [isPaused, setIsPaused] = useState<boolean>(false)
    const [isCountingDown, setIsCountingDown] = useState<boolean>(false)
    useEffect(() => {
        if (errorList.length > 0 && errorList[errorList.length - 1].timestamp > Date.now() - 1000 * TIMEOUT_SECONDS) {
            setActiveError(errorList[errorList.length - 1])
            setIsCountingDown(true)
            setRemaining(TIMEOUT_SECONDS)
        }
    }, [errorList]);

    useEffect(() => {
        if (!isPaused && isCountingDown) {
            const timeoutId = setInterval(() => {
                setRemaining(prev => Math.max(0, prev - 1))
            }, 1000)
            return () => clearTimeout(timeoutId)
        }
    }, [isPaused, isCountingDown]);

    if (activeError != null && remaining == 0 && isCountingDown) {
        setActiveError(null)
        setIsCountingDown(false)
    }

    const closeError = useCallback(() => {
        setActiveError(null)
        setIsCountingDown(false)
    }, [])

    if (isDefault) {
        return (
            <div
                className={` px-4 border-2 rounded-lg flex-wrap transition duration-300`}>
                {"Wrap your app into an ErrorStateDomain"}
            </div>
        )

    }

    return (
        <div className={`py-2`} hidden={errorList.length == 0}>
            {activeError && (
                <div className="flex flex-col px-6 items-start">
                    <div className="relative">
                        <div
                            className={` px-2 pr-8 py-1 border rounded-lg flex-wrap transition duration-500 bg-gray-really`}
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                        >
                            {`Oops, we are having some trouble completing your request. Details: ${activeError.error}`}
                            {remaining > 0 && (
                                <div className="text-xs opacity-70 mt-1 flex items-center gap-1">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setRemaining(0);
                                            setIsCountingDown(false);
                                        }}
                                        className="hover:bg-black hover:bg-opacity-20 rounded p-0.5 transition-colors"
                                        title="Stop countdown"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-3">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                    <span>{`Closing in ${remaining} seconds... ${isPaused ? "paused" : "(hover to pause)"}`}</span>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={closeError}
                            className="absolute top-1 right-1 p-1 hover:bg-black hover:bg-opacity-10 text-black rounded transition-colors"
                            data-testid="error-close-btn"
                            aria-label="Close error"
                        >
                            {closeX}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}


interface ErrorStateDomainProps {
    children: ReactNode
}

function evictOldErrors(errorList: PersistedErrorState[]) {
    const evictedErrorList = errorList.filter((error) => error.timestamp > Date.now() - 1000 * 3600 * 2)
    if (evictedErrorList.length < errorList.length) {
        localStorage.setItem("reported_errors", JSON.stringify(evictedErrorList))
    }
    return evictedErrorList;
}

export function ErrorStateDomain({children}: ErrorStateDomainProps) {
    const [initialized, setInitialized] = useState(false)
    const [errorList, setErrorList] = useState<PersistedErrorState[]>([])

    if(!initialized) {
        const errors = localStorage.getItem("reported_errors")
        const errorList: PersistedErrorState[] = errors != null ? JSON.parse(errors) : []
        const evictedErrorList = evictOldErrors(errorList);
        setErrorList(evictedErrorList)
        setInitialized(true)
    }

    const handleNewError = useCallback((error: string) => {
        setErrorList((current) => {
            const newErrorList = [...current, {
                error,
                timestamp: Date.now()
            }]
            const evictedErrorList = evictOldErrors(newErrorList)
            localStorage.setItem("reported_errors", JSON.stringify(evictedErrorList))
            return newErrorList
        })
    }, [])

    return (
        <ErrorContext.Provider
            value={
                {
                    errorList: errorList,
                    reportError: handleNewError,
                    isDefault: false
                }
            }>
            {children}
        </ErrorContext.Provider>
    )
}

export const useErrorReporter = () => useContext(ErrorContext).reportError